import {Component, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy} from '@angular/core';
import {EnergyBalanceResponse} from '../../../../../api/models/energy-balance-response';
import {Chart, ChartData, ChartOptions, type LegendItem} from 'chart.js';
import {BaseChartComponent} from '../base-chart.component';
import {BaseChartDirective} from 'ng2-charts';
import {de} from 'date-fns/locale';
import {MatIconModule} from '@angular/material/icon';
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'app-yearly-balance-chart',
  templateUrl: '../chart.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [BaseChartDirective, MatIconModule],
})
export class YearBalanceChartComponent extends BaseChartComponent implements OnChanges {
  @Input() data!: EnergyBalanceResponse[];
  useCorrectedValues = true;
  private readonly useCompactTooltip = typeof window !== 'undefined' && window.matchMedia('(max-width: 767.98px)').matches;
  barChartOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          generateLabels(chart: Chart): LegendItem[] {
            function customText(i: number) {
              if (i == 0) {
                //set labels manually for the two columns
                return 'Gesamt-Produktion';
              } else {
                return 'Gesamt-Verbrauch';
              }
            }

            return chart.data.datasets
              .map((dataset, i) => ({
                text: customText(i), // overwrite the textes for the legend labels
                fillStyle: dataset.backgroundColor as string,
                strokeStyle: dataset.borderColor as string,
                lineWidth: 0,
                boxWidth: 30,
                hidden: false,
                datasetIndex: i,
              }))
              .filter((_, i) => i % 3 === 0); //dont show all 4 labels, but only two for the two columsn
          },
        },
      },
      tooltip: {
        enabled: true,
        mode: this.useCompactTooltip ? 'nearest' : 'index',
        intersect: false,
        position: 'nearest',
        backgroundColor: 'rgba(0, 0, 0, 0.38)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: 'rgba(255, 255, 255, 0.25)',
        borderWidth: 1,
        padding: this.useCompactTooltip ? 6 : 10,
        bodySpacing: this.useCompactTooltip ? 3 : 6,
        boxPadding: this.useCompactTooltip ? 2 : 4,
        titleFont: {
          size: this.useCompactTooltip ? 10 : 12,
        },
        bodyFont: {
          size: this.useCompactTooltip ? 10 : 12,
        },
        caretPadding: this.useCompactTooltip ? 8 : 12,
        displayColors: !this.useCompactTooltip,
        callbacks: {
          label: (context) => context.dataset.label + ': ' + context.formattedValue + ' kWh',
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        type: 'time',
        time: {
          unit: 'month',
          tooltipFormat: 'MMM yyyy',
          displayFormats: {
            month: 'MMM',
            day: 'D',
          },
        },
        adapters: {
          date: {
            locale: de,
          },
        },
        ticks: {
          callback: (value) => {
            const date = new Date(value as string);
            return date.toLocaleDateString(environment.locale, {month: 'short', timeZone: this.timezone});
          },
          maxRotation: 0,
          minRotation: 0,
        },

        border: {
          display: false,
        },
      },
      y: {
        stacked: true,
        display: true,
        grid: {
          display: true,
        },
        ticks: {
          stepSize: 50,
        },
        border: {
          display: false,
        },
        offset: true,
      },
    },
  };

  constructor() {
    super();
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log('Yearly Balance Chart received change', changes);
    if (changes['data']) {
      this.onResourceChange();
    }
  }

  private onResourceChange() {
    if (!this.data) {
      return; // Exit if no data is available
    }
    this.chartOptions.set(this.barChartOptions);
    this.chartData.set(this.getChartData());
    //details
    const aggregatedRow = super.getAggregatedBalanceDataRow(this.data, this.useCorrectedValues);
    const dailyData = this.data.map((data) => super.getBalanceDataRow(data, this.useCorrectedValues));
    this.balanceData = [aggregatedRow, ...dailyData];
    //independence
    this.independenceData = [super.getIndependenceDataRow(this.getAggregatedAverageResponse(this.data, this.useCorrectedValues), this.useCorrectedValues)];

    this.chart?.update();
  }

  getChartData(): ChartData<'bar'> {
    const dataList = this.data;
    const labels: string[] = [];
    const fromBatteryData: number[] = [];
    const fromGridData: number[] = [];
    const fromPVData: number[] = [];
    const toPVData: number[] = [];
    const toBatteryData: number[] = [];
    const toGridData: number[] = [];

    dataList.forEach((data) => {
      const selfConsumptionC = this.getSelfConsumptionFromConsumption(data, this.useCorrectedValues);
      const selfConsumptionP = this.getSelfConsumptionFromProduction(data, this.useCorrectedValues);
      const fromBattery = this.getEnergyFromBattery(data, this.useCorrectedValues);
      const fromGrid = data.energyFromGrid ?? 0;
      const toBattery = data.energyToBattery ?? 0;
      const toGrid = this.getEnergyToGrid(data, this.useCorrectedValues);

      // For each EnergyBalanceResponse, we add two labels
      labels.push(data.date!.toString());
      //labels.push(data.date!.toString() + 'V');

      // First bar (Verbrauch)
      fromBatteryData.push(-fromBattery);
      fromGridData.push(-fromGrid);
      fromPVData.push(selfConsumptionC);

      // Second bar (Produktion)
      toPVData.push(selfConsumptionP);
      toBatteryData.push(toBattery);
      toGridData.push(toGrid);
    });

    return {
      labels: labels,
      datasets: [
        {
          label: 'Eigenverbrauch',
          data: toPVData,
          backgroundColor: '#FBBA00',
          stack: 'production',
          // barThickness: 10,
        },
        {
          label: 'Zur Batterie',
          data: toBatteryData,
          backgroundColor: '#FBBA00',
          stack: 'production',
        },
        {
          label: 'In Verkauf',
          data: toGridData,
          backgroundColor: '#FBBA00',
          stack: 'production',
        },
        {
          label: 'Aus Batterie',
          data: fromBatteryData,
          backgroundColor: '#00B6EE',
          stack: 'consumption',
        },
        {
          label: 'Aus Einkauf',
          data: fromGridData,
          backgroundColor: '#00B6EE',
          stack: 'consumption',
        },
        {
          label: 'Eigenverbrauch',
          data: fromPVData,
          backgroundColor: '#00B6EE',
          stack: 'consumption',
        },
      ],
    };
  }
}
